import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Check, Loader2, ArrowRight, XCircle } from "lucide-react";
import confetti from "canvas-confetti";

const MAX_POLLS = 20;      // 20 * 2s ≈ 40s
const POLL_INTERVAL = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | pending_timeout | error
  const [certificateId, setCertificateId] = useState(null);
  const attempts = useRef(0);
  const timer = useRef(null);
  const celebrated = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }

    const poll = async () => {
      attempts.current += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          setCertificateId(r.data.certificate_id || null);
          if (!celebrated.current) {
            celebrated.current = true;
            confetti({ particleCount: 120, spread: 90, origin: { y: 0.4 } });
          }
          return;
        }
        if (r.data.payment_status === "failed" || r.data.payment_status === "expired") {
          setStatus("error");
          return;
        }
        if (attempts.current >= MAX_POLLS) {
          setStatus("pending_timeout");
          return;
        }
        timer.current = setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (attempts.current >= MAX_POLLS) {
          setStatus("error");
        } else {
          timer.current = setTimeout(poll, POLL_INTERVAL);
        }
      }
    };
    poll();
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [sessionId]);

  return (
    <div className="max-w-xl mx-auto px-6 py-16 text-center" data-testid="payment-success-page">
      {status === "polling" && (
        <>
          <Loader2 className="w-10 h-10 text-gold-700 mx-auto animate-spin" />
          <h1 className="font-display text-3xl text-ink-900 mt-6">Confirmation du paiement…</h1>
          <p className="text-muted2 mt-2 text-sm">Nous vérifions votre paiement auprès de Stripe. Cela prend quelques secondes.</p>
        </>
      )}

      {status === "paid" && (
        <>
          <div className="w-14 h-14 rounded-full bg-gold-500/20 mx-auto flex items-center justify-center">
            <Check className="w-8 h-8 text-gold-700" />
          </div>
          <h1 className="font-display text-4xl text-ink-900 mt-6" data-testid="payment-success-heading">Paiement confirmé</h1>
          <p className="text-muted2 mt-2">Votre certificat vient d'être émis. Bienvenue dans le club.</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            {certificateId ? (
              <button
                onClick={() => nav(`/certificates/${certificateId}`)}
                className="px-6 py-3 rounded-full bg-gold-500 text-ink-900 font-medium hover:bg-gold-400 inline-flex items-center justify-center gap-2"
                data-testid="view-certificate-button"
              >
                Voir mon certificat <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <Link
                to="/certificates"
                className="px-6 py-3 rounded-full bg-gold-500 text-ink-900 font-medium hover:bg-gold-400 inline-flex items-center justify-center gap-2"
                data-testid="view-certificates-button"
              >
                Voir mes certificats <ArrowRight className="w-4 h-4" />
              </Link>
            )}
            <Link to="/dashboard" className="px-6 py-3 rounded-full border border-gold-500/50 text-ink-900 hover:bg-gold-500/10">
              Tableau de bord
            </Link>
          </div>
        </>
      )}

      {status === "pending_timeout" && (
        <>
          <Loader2 className="w-10 h-10 text-gold-700 mx-auto" />
          <h1 className="font-display text-3xl text-ink-900 mt-6">Paiement en cours de traitement</h1>
          <p className="text-muted2 mt-2 text-sm">
            Stripe met un peu plus de temps que d'habitude. Vous recevrez votre certificat par email dès la confirmation.
            Vous pouvez fermer cette page en toute sécurité.
          </p>
          <Link to="/dashboard" className="inline-block mt-6 px-6 py-3 rounded-full bg-gold-500 text-ink-900 font-medium hover:bg-gold-400">
            Retour au tableau de bord
          </Link>
        </>
      )}

      {status === "error" && (
        <>
          <XCircle className="w-10 h-10 text-red-500 mx-auto" />
          <h1 className="font-display text-3xl text-ink-900 mt-6">Un problème est survenu</h1>
          <p className="text-muted2 mt-2 text-sm">
            Nous n'avons pas pu confirmer votre paiement. Si vous avez été débité, contactez-nous — sinon vous pouvez
            réessayer.
          </p>
          <div className="mt-6 flex gap-3 justify-center">
            <Link to="/categories" className="px-6 py-3 rounded-full bg-gold-500 text-ink-900 font-medium hover:bg-gold-400">
              Retour aux catégories
            </Link>
            <Link to="/contact" className="px-6 py-3 rounded-full border border-gold-500/50 text-ink-900 hover:bg-gold-500/10">
              Nous contacter
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
